import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '../../types';
import '../PromptsTable/PromptsTable.css';

interface UserFilterProps {
  users: User[];
  selectedEmails: string[];
  onChange: (emails: string[]) => void;
  usersLoading?: boolean;
}

const userFilterDisplayLabel = (u: User): string => {
  const parts = [u.first_name, u.last_name].filter(Boolean);
  const name = parts.join(' ').trim();
  if (name) return `${name} (${u.email})`;
  return u.email;
};

export const UserFilter: React.FC<UserFilterProps> = ({
  users,
  selectedEmails,
  onChange,
  usersLoading = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const label = useMemo(() => {
    if (!selectedEmails.length) return 'All Users';
    if (selectedEmails.length === 1) return selectedEmails[0];
    return `${selectedEmails.length} selected`;
  }, [selectedEmails]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => userFilterDisplayLabel(u).toLowerCase().includes(q));
  }, [users, search]);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="prompts-table__header-filter-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`prompts-table__header-dropdown-trigger${selectedEmails.length ? ' prompts-table__header-dropdown-trigger--active' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Filter by name"
      >
        {label}
      </button>
      {open && (
        <div className="prompts-table__header-dropdown" role="listbox">
          <div className="prompts-table__selected-pills">
            {selectedEmails.map((email) => (
              <span key={email} className="prompts-table__pill">
                <span className="prompts-table__pill-text">{email}</span>
                <button
                  type="button"
                  className="prompts-table__pill-remove"
                  onClick={() => onChange(selectedEmails.filter((item) => item !== email))}
                  aria-label={`Remove ${email}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            className="prompts-table__header-dropdown-search"
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="prompts-table__header-dropdown-option"
            onClick={() => {
              onChange([]);
              setOpen(false);
              setSearch('');
            }}
          >
            All Users
          </button>
          {filtered.map((u) => (
            <button
              key={u.email}
              type="button"
              className={`prompts-table__header-dropdown-option${selectedEmails.includes(u.email) ? ' prompts-table__header-dropdown-option--selected' : ''}`}
              onClick={() => {
                const exists = selectedEmails.includes(u.email);
                onChange(
                  exists
                    ? selectedEmails.filter((item) => item !== u.email)
                    : [...selectedEmails, u.email]
                );
              }}
            >
              {userFilterDisplayLabel(u)}
            </button>
          ))}
          {filtered.length === 0 && users.length > 0 && (
            <div className="prompts-table__header-dropdown-note">No matches — try a different search</div>
          )}
          {usersLoading && (
            <div className="prompts-table__header-dropdown-note">Loading users…</div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserFilter;
